import { formatPeriodDate, formatPeriodDateRange } from './formatPeriodDates';

function toDateOnly(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function todayDateOnly() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function checkRange(startDate, endDate, today = todayDateOnly()) {
  const start = toDateOnly(startDate);
  const end = toDateOnly(endDate);
  if (start && today < start) return 'pending';
  if (end && today > end) return 'expired';
  return 'active';
}

/**
 * Effective access window for a membership:
 * - override_period: ugp.start_date / ugp.end_date
 * - otherwise: linked period dates
 */
export function getEffectiveAccessWindow(member, periodFallback = null) {
  const override = member?.override_period === true;
  if (override) {
    return {
      start: member.start_date ?? null,
      end: member.end_date ?? null,
      source: 'override'
    };
  }
  return {
    start: member?.period_start_date ?? periodFallback?.start_date ?? null,
    end: member?.period_end_date ?? periodFallback?.end_date ?? null,
    source: 'period'
  };
}

/**
 * Grant-status badge from is_active + override_period + effective dates.
 *
 * Core outcomes the roster cares about:
 * - Invalidated (is_active false)
 * - Valid by override date range
 * - Valid by Groups-period date range
 *
 * Also when dates put them out of bounds (same sources):
 * - Pending by override / Groups-period date range
 * - Expired by override / Groups-period date range
 */
function buildGrantBadge({ isActive, overridePeriod, window }) {
  if (!isActive) {
    return {
      key: 'inactive',
      label: 'Invalidated (inactive)',
      tone: 'danger',
      title: 'Membership is_active is false — access is off regardless of dates'
    };
  }

  const rangeStatus = checkRange(window.start, window.end);
  const byOverride = overridePeriod === true;
  const sourceLabel = byOverride ? 'override date range' : 'Groups-period date range';
  const startLabel = formatPeriodDate(window.start);
  const endLabel = formatPeriodDate(window.end);

  if (rangeStatus === 'pending') {
    return {
      key: byOverride ? 'pending-override' : 'pending-period',
      label: `Pending by ${sourceLabel}`,
      tone: 'warn',
      title: startLabel
        ? `Access window starts ${startLabel}`
        : `Access not started yet (${sourceLabel})`
    };
  }

  if (rangeStatus === 'expired') {
    return {
      key: byOverride ? 'expired-override' : 'expired-period',
      label: `Expired by ${sourceLabel}`,
      tone: 'danger',
      title: endLabel
        ? `Access window ended ${endLabel}`
        : `Access window is past (${sourceLabel})`
    };
  }

  return {
    key: byOverride ? 'valid-override' : 'valid-period',
    label: byOverride ? 'Valid by override date range' : 'Valid by Groups-period date range',
    tone: 'ok',
    title: byOverride
      ? 'override_period is true — access uses ugp start/end dates and is in range today'
      : 'Access follows the linked groups-period dates and is in range today'
  };
}

/**
 * Build display badges for Groups member access (gated by can_view_member_access).
 */
export function getMemberAccessDetails(member, periodFallback = null) {
  if (!member) {
    return {
      canLogin: false,
      isActive: false,
      overridePeriod: false,
      accessStatus: 'expired',
      ugpRangeLabel: '',
      periodRangeLabel: '',
      loginBadge: null,
      grantBadge: null,
      badges: []
    };
  }

  const canLogin = member.can_login === true;
  const isActive = member.is_active !== false;
  const overridePeriod = member.override_period === true;
  const window = getEffectiveAccessWindow(member, periodFallback);
  const accessStatus = !isActive ? 'inactive' : checkRange(window.start, window.end);

  const periodStart = member.period_start_date ?? periodFallback?.start_date ?? null;
  const periodEnd = member.period_end_date ?? periodFallback?.end_date ?? null;
  const ugpStart = member.start_date ?? null;
  const ugpEnd = member.end_date ?? null;

  const ugpRangeLabel = formatPeriodDateRange(ugpStart, ugpEnd);
  const periodRangeLabel = formatPeriodDateRange(periodStart, periodEnd);

  const loginBadge = {
    key: 'login',
    label: 'Login',
    icon: canLogin ? 'check' : 'cancel',
    tone: canLogin ? 'ok' : 'danger',
    title: canLogin
      ? 'User can sign in to any product (can_login)'
      : 'User cannot sign in to any product (can_login is false)'
  };

  const grantBadge = buildGrantBadge({ isActive, overridePeriod, window });

  return {
    canLogin,
    isActive,
    overridePeriod,
    accessStatus,
    ugpRangeLabel,
    periodRangeLabel,
    loginBadge,
    grantBadge,
    badges: [loginBadge, grantBadge]
  };
}

/**
 * Profile badges for Sign in (can_login) and AcuitySIS Platform Access on one context.
 * A grant does not turn sign-in on. Sign-in off keeps the grant visible as a warning.
 */
export function getProfileAccessBadges({ canLogin, shellAccess }) {
  const signedIn = canLogin === true;
  const loginBadge = {
    key: 'sign-in',
    label: 'Sign in',
    icon: signedIn ? 'check' : 'cancel',
    tone: signedIn ? 'ok' : 'danger',
    title: signedIn ? 'Can sign in to any product' : 'Cannot sign in to any product'
  };

  if (shellAccess == null) {
    return {
      loginBadge,
      platformBadge: {
        key: 'platform',
        label: 'Platform Access',
        icon: null,
        tone: 'muted',
        title: 'AcuitySIS for this context'
      }
    };
  }

  const granted = shellAccess === true;
  let tone = 'danger';
  let title = 'No AcuitySIS access for this context';
  if (granted && signedIn) {
    tone = 'ok';
    title = 'AcuitySIS for this context';
  } else if (granted) {
    tone = 'warn';
    title = 'AcuitySIS for this context. Sign in is off, so they still cannot sign in.';
  }

  return {
    loginBadge,
    platformBadge: {
      key: 'platform',
      label: 'Platform Access',
      icon: granted ? 'check' : 'cancel',
      tone,
      title
    }
  };
}
